/* eslint-disable @typescript-eslint/no-unused-vars */
// Molecules
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Flex } from '@kanvas/andromeda';
import styled from 'styled-components/native';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { TextTransform } from 'react-native-localized-text';
import { getBottomSpace } from 'react-native-iphone-x-helper';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { RouteProp } from '@react-navigation/native';

// Themes
import colors from 'theme/colors';

// Molecules
import { Header3 } from 'molecules/text';
import PreviewHeader from 'components/molecules/preview-header';
import { translate } from 'locales';
import { FormLabel, InputWithLabel as TextInputWithLabel, TextInputWithLabel as CurrencyInput } from 'components/molecules/input-with-label';

// Atoms
import ButtonWithIcon from 'components/atoms/button-with-icon';
import DropDown from 'components/atoms/dropdown-input';

// Types
import { LeadAction } from 'domain/shared/types/lead-action';
import { Form, initValues } from './types';

// Hooks
import useSubmitForm from 'model/hooks/use-submit-form';
import ActionService from 'domain/shared/services/action-service';
import { useSelector } from 'react-redux';
import { RootState } from 'domain/shared/store';
import useAuth from 'domain/shared/hooks/useAuth';
import { Masks } from 'react-native-mask-input';
import DateTimeInput from 'components/atoms/datetime-picker';
import UserService from 'domain/shared/services/user-service';
import DropDownWithLabel from 'components/molecules/dropdown-with-label';

type ParamList = {
  Any: { action: LeadAction, onGoBack: VoidFunction }
};

interface IProps {
  route: RouteProp<ParamList, 'Any'>;
  navigation: NativeStackNavigationProp<ParamList, 'Any'>;
}

const Container = styled.View`
  flex: 1px;
  background-color: ${colors.WHITE};
`;

const Content = styled(KeyboardAwareScrollView)`
  padding: 20px;
`;

const ButtonContainer = styled(Flex)`
  margin-bottom: ${getBottomSpace()}px;
  padding-vertical: 16px;
  border-top-width: 1px;
  border-top-color: ${colors.BORDER_COLOR};
  padding-bottom: 60px;
`;

const SectionTitle = styled(Header3)`
  margin-vertical: 8px;
  color: ${colors.PRIMARY_TEXT};
`

const checkOptions = [
  { label: 'Yes', value: 'Yes' },
  { label: 'No', value: 'No' },
]

const phoneRegExp = /^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$/;

type ListItem = { label: string, value: string };

const GenerateCoverLetterForm = (props: IProps) => {

  // Props
  const { navigation, route } = props;
  const { action, onGoBack } = route.params;

  // State
  const [loading, setLoading] = useState(false);
  const [linkedFills, setLinkedFills] = useState<Record<string, any>>();
  const [hasCobuyer, setHasCobuyer] = useState<string>();
  const [hasTradeVehicle, setHasTradeVehicle] = useState<string>();
  const [hasPayoff, setHasPayoff] = useState<string>();
  const [managers, setManagers] = useState<ListItem[]>([]);
  const [ownerList, setOwnerList] = useState<ListItem[]>([]);

  // Hooks
  const { submit } = useSubmitForm(action);
  const { user } = useAuth();
  const lead = useSelector((state: RootState) => state.opportunity.activeOpportunity)!;

  const onSubmit = async (values: Form) => {
    try {
      setLoading(true);

      const submitted = await submit(values);
      if (!submitted) return;
      onGoBack?.();
      navigation.goBack();
    } finally {
      setLoading(false);
    }
  };

  const getLinkedFields = async () => {
    if (!lead || !user) return;

    const { uuid: lead_id } = lead;
    const { default_company_uuid: company_id } = user

    setLoading(true);
    const response = await ActionService.getLinkedFields(lead_id, company_id);
    if (response) setLinkedFills(response);
    setLoading(false);
  };

  const config = useMemo(() => {
    if (!action) return [];
    if (action.form_config === null) return [];
    const config = JSON.parse(action.form_config) as Record<string, unknown>;
    return config['hidden'] as string[];
  }, [action]);

  const validationSchema = useMemo(() => {
    const personValidation = Yup.object().shape({
      full_name: Yup.string().required(),
      address_1: Yup.string().required(),
      city: Yup.string().required(),
      state: Yup.string().required(),
      zip: Yup.string().required(),
      home_phone: Yup.string().trim().matches(phoneRegExp, translate('invalidPhone', TextTransform.CAPITAL)).optional(),
      work_phone: Yup.string().trim().matches(phoneRegExp, translate('invalidPhone', TextTransform.CAPITAL)).optional(),
      cell_phone: Yup.string().trim().matches(phoneRegExp, translate('invalidPhone', TextTransform.CAPITAL)).required(),
      email: Yup.string().email().optional(),
      ssn: Yup.string().required(),
      date_of_birth: Yup.string().required(),
      drivers_license: Yup.string().required(),
      dl_state: Yup.string().required(),
      dl_exp_date: Yup.string().required(),
      county: Yup.string().optional(),
      city_town: Yup.string().optional(),
    })

    const validation = Yup.object().shape({
      customer: personValidation,
      cobuyer: Yup.object().optional(),
      purchase_vehicle: Yup.object().shape({
        stock_number: Yup.string().required(),
        condition: Yup.string().required(),
        year: Yup.string().required(),
        make: Yup.string().required(),
        model: Yup.string().required(),
        body_type: Yup.string().optional(),
        color: Yup.string().optional(),
        vin: Yup.string().required(),
        miles: Yup.string().required(),
        warranty: Yup.string().optional(),
        in_service_date: Yup.string().optional()
      }),
      trade_vehicle: hasTradeVehicle === "Yes" ? Yup.object().shape({
        year: Yup.string().required(),
        make: Yup.string().required(),
        model: Yup.string().required(),
        body_type: Yup.string().optional(),
        color: Yup.string().optional(),
        vin: Yup.string().required(),
        miles: Yup.string().required(),
        tag: Yup.string().optional()
      }) : Yup.object().optional(),
      insurance: Yup.object().shape({
        company: Yup.string().optional(),
        street_address: Yup.string().optional(),
        city: Yup.string().optional(),
        state: Yup.string().optional(),
        zip: Yup.string().optional(),
        agent: Yup.string().optional(),
        phone: Yup.string().optional(),
        policy_number: Yup.string().required(),
        expiration_date: Yup.string().required(),
        deductible: Yup.string().optional(),
        full_coverage: Yup.string().optional()
      }),
      payoff: hasPayoff === "Yes" ? Yup.object().shape({
        amount: Yup.string().required(),
        good_through: Yup.string().required(),
        account_number: Yup.string().required(),
        lien_holder_name: Yup.string().required(),
        address: Yup.string().optional(),
        city: Yup.string().optional(),
        state: Yup.string().optional(),
        zip: Yup.string().optional(),
        phone: Yup.string().trim().matches(phoneRegExp, translate('invalidPhone', TextTransform.CAPITAL)).optional(),
      }) : Yup.object().optional(),
      selling_info: Yup.object().shape({
        price: Yup.string().required(),
        rebate: Yup.string().optional(),
        trade: Yup.string().optional(),
        payoff: Yup.string().optional(),
        cash_down: Yup.string().optional(),
        refund_item: Yup.string().optional(),
        refund_value: Yup.string().optional(),
        sales_manager_name: Yup.string().required(),
        sales_person_name: Yup.string().required()
      }),
      hasPayoff: Yup.string().oneOf(["Yes", "No"]).required(),
      hasCobuyer: Yup.string().oneOf(["Yes", "No"]).required(),
      hasTradeVehicle: Yup.string().oneOf(["Yes", "No"]).required()
    });

    return validation;
  }, [config, hasCobuyer, hasTradeVehicle, hasPayoff]);

  const default_company_branch = user?.default_company_branch || 0;

  const getManagers = useCallback(async () => {
    const reponse = await UserService.getAllUserManager(default_company_branch.toString());
    const list = reponse.data.map(({ id = 0, firstname = '', lastname = '' }) => ({
      value: id.toString(),
      label: `${firstname.trim()} ${lastname.trim()}`,
    }));
    setManagers(list);
  }, []);

  const getOwnerList = useCallback(async () => {
    let list: ListItem[] = (await UserService.getAllUser('')).map(({ id = 0, firstname = '', lastname = '' }) => ({
      value: id,
      label: `${firstname.trim()} ${lastname.trim()}`,
      key: id,
    }));

    if (!lead.leads_owner_id) return

    const currentUser = list.some((a) => a.value === lead.leads_owner_id?.toString());

    if (!currentUser) {
      const cuResponse = await UserService.getUser(lead.leads_owner_id);

      if (cuResponse) {
        const cu: ListItem = {
          value: cuResponse.id.toString(),
          label: `${cuResponse.firstname.trim()} ${cuResponse.lastname.trim()}`,
        };

        list = list.concat(cu);
      }
    }
    setOwnerList(list);
  }, []);

  const {
    handleSubmit,
    handleChange,
    handleBlur,
    setFieldValue,
    setValues,
    validateForm,
    values,
    errors,
    touched,
  } = useFormik<Form>({
    initialValues: {
      ...initValues,
      selling_info: {
        ...initValues.selling_info,
        sales_manager_name: "",
        sales_person_name: lead.leads_owner_id?.toString() || user?.id
      },
      hasCobuyer: "",
      hasPayoff: "",
      hasTradeVehicle: ""
    } as Form,
    onSubmit,
    validationSchema
  });

  useEffect(() => {
    if (lead?.custom_fields?.manager_id) {
      setFieldValue('selling_info.sales_manager_name', lead.custom_fields.manager_id)
    }
  }, [lead]);

  useEffect(() => {
    if (linkedFills) {
      const hasTradeVehicleCheck = linkedFills["lead.trade-in.vin"] !== null || linkedFills["lead.vehicle-info.model"] || linkedFills["lead.vehicle-info.year"] || linkedFills["lead.vehicle-info.make"] || linkedFills["lead.vehicle-info.odometer"]

      setHasCobuyer(hasTradeVehicleCheck ? "Yes" : undefined);
      setHasTradeVehicle(hasTradeVehicleCheck ? "Yes" : undefined)

      setValues({
        ...values,
        customer: {
          ...values.customer,
          full_name: `${linkedFills["contact.firstname"] ?? ""} ${linkedFills["contact.lastname"] ?? ""}`,
          address_1: linkedFills["contact.address.address"] ?? "",
          city: linkedFills["contact.address.city"] ?? "",
          state: linkedFills["contact.address.state"] ?? "",
          zip: linkedFills["contact.address.zip"] ?? "",
          cell_phone: linkedFills["contact.phone"] ?? "",
          email: linkedFills["contact.email"] ?? "",
          date_of_birth: linkedFills["contact.dob"] ?? linkedFills["contact.dob_new_formt"] ?? "",
          ssn: linkedFills["contact.ssn"] ?? "",
          home_phone: linkedFills["contact.home_phone"] ?? "",
          work_phone: linkedFills["contact.work_phone"] ?? ""
        },
        hasCobuyer: linkedFills["cobuyer.name"] !== null ? "Yes" : undefined,
        hasTradeVehicle: hasTradeVehicleCheck ? "Yes" : undefined,
        cobuyer: {
          ...values.cobuyer,
          full_name: linkedFills["cobuyer.name"] ?? ""
        },
        payoff: {
          ...values.payoff,
          account_number: linkedFills["lead.payoff.bank.account_number"] ?? "",
          address: linkedFills["lead.payoff.bank.street"] ?? "",
          amount: linkedFills["lead.payoff.bank.amount"] ?? "",
          city: linkedFills["lead.payoff.bank.city"] ?? "",
          phone: linkedFills["lead.payoff.bank.amount"] ?? "",
          state: linkedFills["lead.payoff.bank.state"] ?? "",
          zip: linkedFills["lead.payoff.bank.zip"] ?? ""
        },
        insurance: {
          ...values.insurance,
          agent: linkedFills["insurance.agent.name"] ?? "",
          city: linkedFills["insurance.agent.name"] ?? "",
          policy_number: linkedFills["insurance.carrier.policy_name"] ?? "",
          phone: linkedFills["insurance.agent.phone"] ?? "",
          expiration_date: linkedFills["insurance.carrier.end_date"] ?? "",
          full_coverage: linkedFills["insurance.carrier.coverage"] ?? ""
        },
        purchase_vehicle: {
          ...values.purchase_vehicle,
          in_service_date: linkedFills["lead.vehicle-info.in_service_date"] ?? "",
          miles: linkedFills["lead.vehicle-info.odometer"] ?? "",
          vin: linkedFills["lead.vehicle-info.vin"] ?? "",
          model: linkedFills["lead.vehicle-info.model"] ?? "",
          year: linkedFills["lead.vehicle-info.year"] ?? "",
          condition: linkedFills["lead.vehicle-info.condition"]?.toString()?.toLowerCase() ?? "",
          make: linkedFills["lead.vehicle-info.make"] ?? "",
          stock_number: linkedFills["lead.vehicle-info.stk_number"] ?? ""
        },
        trade_vehicle: {
          ...values.trade_vehicle,
          year: linkedFills["lead.trade-in.year"] ?? "",
          color: linkedFills["lead.trade-in.color"] ?? "",
          make: linkedFills["lead.trade-in.make"] ?? "",
          vin: linkedFills["lead.trade-in.vin"] ?? "",
          model: linkedFills["lead.trade-in.model"] ?? "",
          miles: linkedFills["lead.trade-in.odometer"] ?? "",
          body_type: linkedFills["lead.trade-in.body_type"] ?? "",
          tag: linkedFills["lead.trade-in.tag-number"] ?? ""
        },
        selling_info: {
          ...values.selling_info,
          price: linkedFills["prefill.selling-price"] ?? "",
        }
      });
    }
  }, [linkedFills]);

  useEffect(() => {
    getLinkedFields();
  }, [lead, user]);

  useEffect(() => {
    getManagers()
    getOwnerList()
  }, []);

  return (
    <Container>
      <PreviewHeader
        actionName={translate('generateCoverLetter', TextTransform.CAPITAL)}
        onBack={() => navigation.goBack()}
      />

      <Content
        showsVerticalScrollIndicator={false}
        extraScrollHeight={100}
      >
        {/* Customer Info */}
        <>
          <SectionTitle text={translate('customerInfo', TextTransform.CAPITALIZE)} />

          <TextInputWithLabel
            label={translate('fullName', TextTransform.CAPITALIZE)}
            onChangeText={handleChange('customer.full_name')}
            onBlur={handleBlur('customer.full_name')}
            value={values?.customer.full_name}
            errors={touched?.customer?.full_name ? errors?.customer?.full_name : ''}
            required
          />

          <TextInputWithLabel
            label={translate('address', TextTransform.CAPITALIZE)}
            onChangeText={handleChange('customer.address_1')}
            onBlur={handleBlur('customer.address_1')}
            value={values?.customer.address_1}
            errors={touched?.customer?.address_1 ? errors?.customer?.address_1 : ''}
            required
          />

          <TextInputWithLabel
            label={translate('city', TextTransform.CAPITALIZE)}
            onChangeText={handleChange('customer.city')}
            onBlur={handleBlur('customer.city')}
            value={values?.customer.city}
            errors={touched?.customer?.city ? errors?.customer?.city : ''}
            required
          />

          <TextInputWithLabel
            label={translate('state', TextTransform.CAPITALIZE)}
            onChangeText={handleChange('customer.state')}
            onBlur={handleBlur('customer.state')}
            value={values?.customer.state}
            errors={touched?.customer?.state ? errors?.customer?.state : ''}
            required
          />

          <TextInputWithLabel
            label={translate('zip', TextTransform.CAPITALIZE)}
            onChangeText={handleChange('customer.zip')}
            onBlur={handleBlur('customer.zip')}
            value={values?.customer.zip}
            errors={touched?.customer?.zip ? errors?.customer?.zip : ''}
            required
          />

          <TextInputWithLabel
            label={translate('homePhone', TextTransform.CAPITALIZE)}
            placeholder={translate('homePhone', TextTransform.CAPITALIZE)}
            onChangeText={handleChange('customer.home_phone')}
            onBlur={handleBlur('customer.home_phone')}
            value={values.customer.home_phone}
            errors={touched?.customer?.home_phone ? errors?.customer?.home_phone : ''}
            keyboardType="phone-pad"
            mask={Masks.USA_PHONE}
          />

          <TextInputWithLabel
            label={translate('workPhone', TextTransform.CAPITALIZE)}
            placeholder={translate('workPhone', TextTransform.CAPITALIZE)}
            onChangeText={handleChange('customer.work_phone')}
            onBlur={handleBlur('customer.work_phone')}
            value={values.customer.work_phone}
            errors={touched?.customer?.work_phone ? errors?.customer?.work_phone : ''}
            keyboardType="phone-pad"
            mask={Masks.USA_PHONE}
          />

          <TextInputWithLabel
            label={translate('cellPhone', TextTransform.CAPITALIZE)}
            placeholder={translate('cellPhone', TextTransform.CAPITALIZE)}
            onChangeText={handleChange('customer.cell_phone')}
            onBlur={handleBlur('customer.cell_phone')}
            value={values.customer.cell_phone}
            errors={touched?.customer?.cell_phone ? errors?.customer?.cell_phone : ''}
            keyboardType="phone-pad"
            mask={Masks.USA_PHONE}
            required
          />

          <TextInputWithLabel
            label={translate('email', TextTransform.CAPITALIZE)}
            blurOnSubmit
            returnKeyType="done"
            onChangeText={handleChange('customer.email')}
            onBlur={handleBlur('customer.email')}
            value={values.customer.email}
            errors={touched?.customer?.email ? errors?.customer?.email : ''}
            keyboardType="email-address"
          />

          <TextInputWithLabel
            label={translate('ssn', TextTransform.UPPERCASE)}
            onChangeText={handleChange('customer.ssn')}
            onBlur={handleBlur('customer.ssn')}
            value={values?.customer.ssn}
            errors={touched?.customer?.ssn ? errors?.customer?.ssn : ''}
            required
          />

          <DateTimeInput
            label={translate('dob')}
            placeholder="MM-DD-YY"
            showIcon={false}
            onChange={handleChange('customer.date_of_birth')}
            value={values?.customer.date_of_birth}
            errors={touched?.customer?.date_of_birth ? errors?.customer?.date_of_birth : ''}
            required
          />

          <TextInputWithLabel
            label={`${translate('upload-docs.dl', TextTransform.CAPITALIZE)} #`}
            onChangeText={handleChange('customer.drivers_license')}
            onBlur={handleBlur('customer.drivers_license')}
            value={values?.customer.drivers_license}
            errors={touched?.customer?.drivers_license ? errors?.customer?.drivers_license : ''}
            required
          />

          <TextInputWithLabel
            label={translate('dl_state', TextTransform.CAPITALIZE)}
            onChangeText={handleChange('customer.dl_state')}
            onBlur={handleBlur('customer.dl_state')}
            value={values?.customer.dl_state}
            errors={touched?.customer?.dl_state ? errors?.customer?.dl_state : ''}
            required
          />

          <DateTimeInput
            label={translate('dlExpDate', TextTransform.CAPITALIZE)}
            placeholder="MM-DD-YY"
            showIcon={false}
            onChange={handleChange('customer.dl_exp_date')}
            value={values?.customer.dl_exp_date}
            errors={touched?.customer?.dl_exp_date ? errors?.customer?.dl_exp_date : ''}
            required
          />

          <TextInputWithLabel
            label={translate('county', TextTransform.CAPITALIZE)}
            onChangeText={handleChange('customer.county')}
            onBlur={handleBlur('customer.county')}
            value={values?.customer.county}
            errors={touched?.customer?.county ? errors?.customer?.county : ''}
          />

          <TextInputWithLabel
            label={translate('city', TextTransform.CAPITALIZE)}
            onChangeText={handleChange('customer.city_town')}
            onBlur={handleBlur('customer.city_town')}
            value={values?.customer.city_town}
            errors={touched?.customer?.city_town ? errors?.customer?.city_town : ''}
          />
        </>

        <SectionTitle text={translate('cobuyerInfo', TextTransform.CAPITALIZE)} />

        <DropDownWithLabel
          label={`${translate('cobuyer', TextTransform.CAPITAL)}*`}
          items={checkOptions}
          customValue={values.hasCobuyer}
          defaultValue={values.hasCobuyer}
          onItemSelected={(item: any) => {
            setFieldValue('hasCobuyer', item?.value);
            setHasCobuyer(item?.value);
          }}
          errors={touched.hasCobuyer ? errors.hasCobuyer : ""}
          style={{
            marginVertical: 8
          }}
        />

        {/* Cobuyer Info */}
        {values.hasCobuyer === "Yes" && (
          <>
            <TextInputWithLabel
              label={translate('fullName', TextTransform.CAPITALIZE)}
              onChangeText={handleChange('cobuyer.full_name')}
              onBlur={handleBlur('cobuyer.full_name')}
              value={values?.cobuyer.full_name}
              errors={touched?.cobuyer?.full_name ? errors?.cobuyer?.full_name : ''}
            />

            <TextInputWithLabel
              label={translate('address', TextTransform.CAPITALIZE)}
              onChangeText={handleChange('cobuyer.address_1')}
              onBlur={handleBlur('cobuyer.address_1')}
              value={values?.cobuyer.address_1}
              errors={touched?.cobuyer?.address_1 ? errors?.cobuyer?.address_1 : ''}
            />

            <TextInputWithLabel
              label={translate('city', TextTransform.CAPITALIZE)}
              onChangeText={handleChange('cobuyer.city')}
              onBlur={handleBlur('cobuyer.city')}
              value={values?.cobuyer.city}
              errors={touched?.cobuyer?.city ? errors?.cobuyer?.city : ''}
            />

            <TextInputWithLabel
              label={translate('state', TextTransform.CAPITALIZE)}
              onChangeText={handleChange('cobuyer.state')}
              onBlur={handleBlur('cobuyer.state')}
              value={values?.cobuyer.state}
              errors={touched?.cobuyer?.state ? errors?.cobuyer?.state : ''}
            />

            <TextInputWithLabel
              label={translate('zip', TextTransform.CAPITALIZE)}
              onChangeText={handleChange('cobuyer.zip')}
              onBlur={handleBlur('cobuyer.zip')}
              value={values?.cobuyer.zip}
              errors={touched?.cobuyer?.zip ? errors?.cobuyer?.zip : ''}
            />

            <TextInputWithLabel
              label={translate('homePhone', TextTransform.CAPITALIZE)}
              placeholder={translate('homePhone', TextTransform.CAPITALIZE)}
              onChangeText={handleChange('cobuyer.home_phone')}
              onBlur={handleBlur('cobuyer.home_phone')}
              value={values.cobuyer.home_phone}
              errors={touched?.cobuyer?.home_phone ? errors?.cobuyer?.home_phone : ''}
              keyboardType="phone-pad"
              mask={Masks.USA_PHONE}
            />

            <TextInputWithLabel
              label={translate('workPhone', TextTransform.CAPITALIZE)}
              placeholder={translate('workPhone', TextTransform.CAPITALIZE)}
              onChangeText={handleChange('cobuyer.work_phone')}
              onBlur={handleBlur('cobuyer.work_phone')}
              value={values.cobuyer.work_phone}
              errors={touched?.cobuyer?.work_phone ? errors?.cobuyer?.work_phone : ''}
              keyboardType="phone-pad"
              mask={Masks.USA_PHONE}
            />

            <TextInputWithLabel
              label={translate('cellPhone', TextTransform.CAPITALIZE)}
              placeholder={translate('cellPhone', TextTransform.CAPITALIZE)}
              onChangeText={handleChange('cobuyer.cell_phone')}
              onBlur={handleBlur('cobuyer.cell_phone')}
              value={values.cobuyer.cell_phone}
              errors={touched?.cobuyer?.cell_phone ? errors?.cobuyer?.cell_phone : ''}
              keyboardType="phone-pad"
              mask={Masks.USA_PHONE}
            />

            <TextInputWithLabel
              label={translate('email', TextTransform.CAPITALIZE)}
              blurOnSubmit
              returnKeyType="done"
              onChangeText={handleChange('cobuyer.email')}
              onBlur={handleBlur('cobuyer.email')}
              value={values.cobuyer.email}
              errors={touched?.cobuyer?.email ? errors?.cobuyer?.email : ''}
              keyboardType="email-address"
            />

            <TextInputWithLabel
              label={translate('ssn', TextTransform.UPPERCASE)}
              onChangeText={handleChange('cobuyer.ssn')}
              onBlur={handleBlur('cobuyer.ssn')}
              value={values?.cobuyer.ssn}
              errors={touched?.cobuyer?.ssn ? errors?.cobuyer?.ssn : ''}
            />

            <DateTimeInput
              label={translate('dob')}
              placeholder="MM-DD-YY"
              showIcon={false}
              onChange={handleChange('cobuyer.date_of_birth')}
              value={values?.cobuyer.date_of_birth}
              errors={touched?.cobuyer?.date_of_birth ? errors?.cobuyer?.date_of_birth : ''}
            />

            <TextInputWithLabel
              label={translate('upload-docs.dl', TextTransform.CAPITALIZE)}
              onChangeText={handleChange('cobuyer.drivers_license')}
              onBlur={handleBlur('cobuyer.drivers_license')}
              value={values?.cobuyer.drivers_license}
              errors={touched?.cobuyer?.drivers_license ? errors?.cobuyer?.drivers_license : ''}
            />

            <TextInputWithLabel
              label={translate('dl_state', TextTransform.CAPITALIZE)}
              onChangeText={handleChange('cobuyer.dl_state')}
              onBlur={handleBlur('cobuyer.dl_state')}
              value={values?.cobuyer.dl_state}
              errors={touched?.cobuyer?.dl_state ? errors?.cobuyer?.dl_state : ''}
            />

            <DateTimeInput
              label={translate('dlExpDate', TextTransform.CAPITALIZE)}
              placeholder="MM-DD-YY"
              showIcon={false}
              onChange={handleChange('cobuyer.dl_exp_date')}
              value={values?.cobuyer.dl_exp_date}
              errors={touched?.cobuyer?.dl_exp_date ? errors?.cobuyer?.dl_exp_date : ''}
            />

            <TextInputWithLabel
              label={translate('county', TextTransform.CAPITALIZE)}
              onChangeText={handleChange('cobuyer.county')}
              onBlur={handleBlur('cobuyer.county')}
              value={values?.cobuyer.county}
              errors={touched?.cobuyer?.county ? errors?.cobuyer?.county : ''}
            />

            <TextInputWithLabel
              label={translate('city', TextTransform.CAPITALIZE)}
              onChangeText={handleChange('cobuyer.city_town')}
              onBlur={handleBlur('cobuyer.city_town')}
              value={values?.cobuyer.city_town}
              errors={touched?.cobuyer?.city_town ? errors?.cobuyer?.city_town : ''}
            />
          </>
        )}

        {/* Purchase Vehicle Info */}
        <>
          <SectionTitle text={translate('purchaseVehInfo', TextTransform.CAPITALIZE)} />

          <TextInputWithLabel
            label={`${translate('stock', TextTransform.CAPITALIZE)} #`}
            onChangeText={handleChange('purchase_vehicle.stock_number')}
            onBlur={handleBlur('purchase_vehicle.stock_number')}
            value={values?.purchase_vehicle.stock_number}
            errors={touched?.purchase_vehicle?.stock_number ? errors?.purchase_vehicle?.stock_number : ''}
            required
          />

          <DropDownWithLabel
            label={`${translate('vehicleCondition', TextTransform.CAPITAL)}*`}
            items={[
              { label: translate('new', TextTransform.CAPITAL), value: 'new' },
              { label: translate('used', TextTransform.CAPITAL), value: 'used' },
              { label: translate('demo', TextTransform.CAPITAL), value: 'demo' },
            ]}
            customValue={values.purchase_vehicle.condition}
            defaultValue={values.purchase_vehicle.condition}
            onItemSelected={(item: any) => {
              setFieldValue('purchase_vehicle.condition', item?.value);
            }}
            errors={touched?.purchase_vehicle?.condition ? errors.purchase_vehicle?.condition : ""}
            style={{
              marginVertical: 8
            }}
          />

          <TextInputWithLabel
            label={translate('year', TextTransform.CAPITALIZE)}
            onChangeText={handleChange('purchase_vehicle.year')}
            onBlur={handleBlur('purchase_vehicle.year')}
            value={values?.purchase_vehicle.year}
            errors={touched.purchase_vehicle?.year ? errors.purchase_vehicle?.year : ''}
            keyboardType='numeric'
            required
          />

          <TextInputWithLabel
            label={translate('make', TextTransform.CAPITALIZE)}
            onChangeText={handleChange('purchase_vehicle.make')}
            onBlur={handleBlur('purchase_vehicle.make')}
            value={values?.purchase_vehicle.make}
            errors={touched.purchase_vehicle?.make ? errors.purchase_vehicle?.make : ''}
            required
          />

          <TextInputWithLabel
            label={translate('model', TextTransform.CAPITALIZE)}
            onChangeText={handleChange('purchase_vehicle.model')}
            onBlur={handleBlur('purchase_vehicle.model')}
            value={values?.purchase_vehicle.model}
            errors={touched.purchase_vehicle?.model ? errors.purchase_vehicle?.model : ''}
            required
          />

          <TextInputWithLabel
            label={translate('bodyType', TextTransform.CAPITALIZE)}
            onChangeText={handleChange('purchase_vehicle.body_type')}
            onBlur={handleBlur('purchase_vehicle.body_type')}
            value={values?.purchase_vehicle.body_type}
            errors={touched.purchase_vehicle?.body_type ? errors.purchase_vehicle?.body_type : ''}
          />

          <TextInputWithLabel
            label={translate('color', TextTransform.CAPITALIZE)}
            onChangeText={handleChange('purchase_vehicle.color')}
            onBlur={handleBlur('purchase_vehicle.color')}
            value={values?.purchase_vehicle.color}
            errors={touched.purchase_vehicle?.color ? errors.purchase_vehicle?.color : ''}
          />

          <TextInputWithLabel
            label={`${translate('vin', TextTransform.CAPITALIZE)} #`}
            placeholder="0"
            onChangeText={handleChange('purchase_vehicle.vin')}
            onBlur={handleBlur('purchase_vehicle.vin')}
            value={values?.purchase_vehicle.vin}
            errors={touched.purchase_vehicle?.vin ? errors.purchase_vehicle?.vin : ''}
            keyboardType='numeric'
            required
          />

          <TextInputWithLabel
            label={`${translate('mileage', TextTransform.CAPITALIZE)}`}
            placeholder="0"
            onChangeText={handleChange('purchase_vehicle.miles')}
            onBlur={handleBlur('purchase_vehicle.miles')}
            value={values?.purchase_vehicle.miles}
            errors={touched.purchase_vehicle?.miles ? errors.purchase_vehicle?.miles : ''}
            keyboardType='numeric'
            required
          />

          {values.purchase_vehicle.condition !== "new" && (
            <TextInputWithLabel
              label={translate('warranty', TextTransform.CAPITALIZE)}
              onChangeText={handleChange('purchase_vehicle.warranty')}
              onBlur={handleBlur('purchase_vehicle.warranty')}
              value={values?.purchase_vehicle.warranty}
              errors={touched.purchase_vehicle?.warranty ? errors.purchase_vehicle?.warranty : ''}
            />
          )}

          {Number(values.purchase_vehicle.miles ?? 0) < 12000 && (
            <DateTimeInput
              label={translate('inServiceDate', TextTransform.CAPITALIZE)}
              placeholder="MM-DD-YY"
              showIcon={false}
              onChange={handleChange('purchase_vehicle.in_service_date')}
              value={values?.purchase_vehicle.in_service_date ?? ""}
              errors={touched?.purchase_vehicle?.in_service_date ? errors?.purchase_vehicle?.in_service_date : ''}
            />
          )}
        </>

        <SectionTitle text={translate('tradeVehInfo', TextTransform.CAPITALIZE)} />

        <DropDownWithLabel
          label={`${translate('tradeVehicle', TextTransform.CAPITAL)}*`}
          items={checkOptions}
          customValue={values.hasTradeVehicle}
          defaultValue={values.hasTradeVehicle}
          onItemSelected={(item: any) => {
            setFieldValue('hasTradeVehicle', item?.value);
            setHasTradeVehicle(item?.value)
          }}
          errors={touched.hasTradeVehicle ? errors.hasTradeVehicle : ""}
          style={{
            marginVertical: 8
          }}
        />

        {/* Trade Vehicle */}
        {values.hasTradeVehicle === "Yes" && (
          <>
            <TextInputWithLabel
              label={translate('year', TextTransform.CAPITALIZE)}
              onChangeText={handleChange('trade_vehicle.year')}
              onBlur={handleBlur('trade_vehicle.year')}
              value={values?.trade_vehicle.year}
              errors={touched.trade_vehicle?.year ? errors.trade_vehicle?.year : ''}
              keyboardType='numeric'
              required
            />

            <TextInputWithLabel
              label={translate('make', TextTransform.CAPITALIZE)}
              onChangeText={handleChange('trade_vehicle.make')}
              onBlur={handleBlur('trade_vehicle.make')}
              value={values?.trade_vehicle.make}
              errors={touched.trade_vehicle?.make ? errors.trade_vehicle?.make : ''}
              required
            />

            <TextInputWithLabel
              label={translate('model', TextTransform.CAPITALIZE)}
              onChangeText={handleChange('trade_vehicle.model')}
              onBlur={handleBlur('trade_vehicle.model')}
              value={values?.trade_vehicle.model}
              errors={touched.trade_vehicle?.model ? errors.trade_vehicle?.model : ''}
              required
            />

            <TextInputWithLabel
              label={translate('bodyType', TextTransform.CAPITALIZE)}
              onChangeText={handleChange('trade_vehicle.body_type')}
              onBlur={handleBlur('trade_vehicle.body_type')}
              value={values?.trade_vehicle.body_type}
              errors={touched.trade_vehicle?.body_type ? errors.trade_vehicle?.body_type : ''}
            />

            <TextInputWithLabel
              label={translate('color', TextTransform.CAPITALIZE)}
              onChangeText={handleChange('trade_vehicle.color')}
              onBlur={handleBlur('trade_vehicle.color')}
              value={values?.trade_vehicle.color}
              errors={touched.trade_vehicle?.color ? errors.trade_vehicle?.color : ''}
            />

            <TextInputWithLabel
              label={`${translate('vin', TextTransform.CAPITALIZE)} #`}
              placeholder="0"
              onChangeText={handleChange('trade_vehicle.vin')}
              onBlur={handleBlur('trade_vehicle.vin')}
              value={values?.trade_vehicle.vin}
              errors={touched.trade_vehicle?.vin ? errors.trade_vehicle?.vin : ''}
              keyboardType='numeric'
              required
            />

            <TextInputWithLabel
              label={`${translate('mileage', TextTransform.CAPITALIZE)}`}
              placeholder="0"
              onChangeText={handleChange('trade_vehicle.miles')}
              onBlur={handleBlur('trade_vehicle.miles')}
              value={values?.trade_vehicle.miles}
              errors={touched.trade_vehicle?.miles ? errors.trade_vehicle?.miles : ''}
              keyboardType='numeric'
              required
            />

            <TextInputWithLabel
              label={`${translate('tag', TextTransform.CAPITALIZE)} #`}
              placeholder="0"
              onChangeText={handleChange('trade_vehicle.tag')}
              onBlur={handleBlur('trade_vehicle.tag')}
              value={values?.trade_vehicle.tag}
              errors={touched.trade_vehicle?.tag ? errors.trade_vehicle?.tag : ''}
              keyboardType='numeric'
            />
          </>
        )}

        {/* Insurance */}
        <>
          <SectionTitle text={translate('insuranceInfo', TextTransform.CAPITALIZE)} />

          <TextInputWithLabel
            label={translate('insuranceCompany', TextTransform.CAPITALIZE)}
            onChangeText={handleChange('insurance.company')}
            onBlur={handleBlur('insurance.company')}
            value={values?.insurance.company}
            errors={touched.insurance?.company ? errors.insurance?.company : ''}
          />

          <TextInputWithLabel
            label={translate('address', TextTransform.CAPITALIZE)}
            onChangeText={handleChange('insurance.street_address')}
            onBlur={handleBlur('insurance.street_address')}
            value={values?.insurance.street_address}
            errors={touched.insurance?.street_address ? errors.insurance?.street_address : ''}
          />

          <TextInputWithLabel
            label={translate('city', TextTransform.CAPITALIZE)}
            onChangeText={handleChange('insurance.city')}
            onBlur={handleBlur('insurance.city')}
            value={values?.insurance.city}
            errors={touched.insurance?.city ? errors.insurance?.city : ''}
          />

          <TextInputWithLabel
            label={translate('state', TextTransform.CAPITALIZE)}
            onChangeText={handleChange('insurance.state')}
            onBlur={handleBlur('insurance.state')}
            value={values?.insurance.state}
            errors={touched.insurance?.state ? errors.insurance?.state : ''}
          />

          <TextInputWithLabel
            label={translate('zip', TextTransform.CAPITALIZE)}
            onChangeText={handleChange('insurance.zip')}
            onBlur={handleBlur('insurance.zip')}
            value={values?.insurance.zip}
            errors={touched.insurance?.zip ? errors.insurance?.zip : ''}
          />

          <TextInputWithLabel
            label={translate('agent', TextTransform.CAPITALIZE)}
            onChangeText={handleChange('insurance.agent')}
            onBlur={handleBlur('insurance.agent')}
            value={values?.insurance.agent}
            errors={touched.insurance?.agent ? errors.insurance?.agent : ''}
          />

          <TextInputWithLabel
            label={translate('phone', TextTransform.CAPITALIZE)}
            placeholder={translate('phone', TextTransform.CAPITALIZE)}
            onChangeText={handleChange('insurance.phone')}
            onBlur={handleBlur('insurance.phone')}
            value={values.insurance.phone}
            errors={touched.insurance?.phone ? errors.insurance?.phone : ''}
            keyboardType="phone-pad"
            mask={Masks.USA_PHONE}
          />

          <TextInputWithLabel
            label={translate('policy', TextTransform.CAPITALIZE)}
            onChangeText={handleChange('insurance.policy_number')}
            onBlur={handleBlur('insurance.policy_number')}
            value={values?.insurance.policy_number}
            errors={touched.insurance?.policy_number ? errors.insurance?.policy_number : ''}
            keyboardType='numeric'
            required
          />

          <DateTimeInput
            label={translate('expirationDate', TextTransform.CAPITALIZE)}
            placeholder="MM-DD-YY"
            showIcon={false}
            onChange={handleChange('insurance.expiration_date')}
            value={values?.insurance.expiration_date}
            errors={touched.insurance?.expiration_date ? errors.insurance?.expiration_date : ''}
            required
          />

          <CurrencyInput
            label="deductible"
            placeholder={translate('deductible', TextTransform.CAPITALIZE)}
            handleChange={handleChange('insurance.deductible')}
            onBlur={handleBlur('insurance.deductible')}
            value={values?.insurance.deductible}
            error={touched.insurance?.deductible ? errors.insurance?.deductible : ''}
            keyboardType="decimal-pad"
            type="currency"
            showErrorMsg={false}
          />

          <Flex style={{ marginVertical: 4 }}>
            <FormLabel text={translate('fullCoverage', TextTransform.CAPITAL)} />
            <DropDown
              items={checkOptions}
              placeHolder=''
              value={values.insurance.full_coverage}
              onSelectItem={(item: any) => {
                setFieldValue('insurance.full_coverage', item?.value);
              }}
              errorsRefs={touched.insurance?.full_coverage ? errors.insurance?.full_coverage : ''}
            />
          </Flex>
        </>

        <SectionTitle text={translate('payoffInfo', TextTransform.CAPITALIZE)} />

        <DropDownWithLabel
          label={`${translate('payoff', TextTransform.CAPITAL)}*`}
          items={checkOptions}
          customValue={hasPayoff}
          defaultValue={values.hasPayoff}
          onItemSelected={(item: any) => {
            setFieldValue('hasPayoff', item?.value);
            setHasPayoff(item?.value)
          }}
          errors={touched.hasPayoff ? errors.hasPayoff : ""}
          style={{
            marginVertical: 8
          }}
        />

        {/* Payoff Info */}
        {values.hasPayoff === "Yes" && (
          <>
            <CurrencyInput
              label="amount"
              placeholder={translate('amount', TextTransform.CAPITALIZE)}
              handleChange={handleChange('payoff.amount')}
              onBlur={handleBlur('payoff.amount')}
              value={values?.payoff.amount}
              error={touched.payoff?.amount ? errors.payoff?.amount : ''}
              keyboardType="decimal-pad"
              required
              type="currency"
              showErrorMsg={false}
            />

            <DateTimeInput
              label={translate('goodThrough', TextTransform.CAPITALIZE)}
              placeholder="MM-DD-YY"
              showIcon={false}
              onChange={handleChange('payoff.good_through')}
              value={values?.payoff.good_through}
              errors={touched.payoff?.good_through ? errors.payoff?.good_through : ''}
              required
            />

            <TextInputWithLabel
              label={translate('accountNumber', TextTransform.CAPITALIZE)}
              placeholder={translate('accountNumber', TextTransform.CAPITALIZE)}
              onChangeText={handleChange('payoff.account_number')}
              onBlur={handleBlur('payoff.account_number')}
              value={values?.payoff.account_number}
              errors={touched.payoff?.account_number ? errors.payoff?.account_number : ''}
              required
            />

            <TextInputWithLabel
              label={translate('lienHolderName', TextTransform.CAPITALIZE)}
              placeholder={translate('lienHolderName', TextTransform.CAPITALIZE)}
              onChangeText={handleChange('payoff.lien_holder_name')}
              onBlur={handleBlur('payoff.lien_holder_name')}
              value={values?.payoff.lien_holder_name}
              errors={touched.payoff?.lien_holder_name ? errors.payoff?.lien_holder_name : ''}
              required
            />

            <TextInputWithLabel
              label={translate('address', TextTransform.CAPITALIZE)}
              onChangeText={handleChange('payoff.address')}
              onBlur={handleBlur('payoff.address')}
              value={values?.payoff.address}
              errors={touched?.payoff?.address ? errors?.payoff?.address : ''}
            />

            <TextInputWithLabel
              label={translate('city', TextTransform.CAPITALIZE)}
              onChangeText={handleChange('payoff.city')}
              onBlur={handleBlur('payoff.city')}
              value={values?.payoff.city}
              errors={touched?.payoff?.city ? errors?.payoff?.city : ''}
            />

            <TextInputWithLabel
              label={translate('state', TextTransform.CAPITALIZE)}
              onChangeText={handleChange('payoff.state')}
              onBlur={handleBlur('payoff.state')}
              value={values?.payoff.state}
              errors={touched?.payoff?.state ? errors?.payoff?.state : ''}
            />

            <TextInputWithLabel
              label={translate('zip', TextTransform.CAPITALIZE)}
              onChangeText={handleChange('payoff.zip')}
              onBlur={handleBlur('payoff.zip')}
              value={values?.payoff.zip}
              errors={touched?.payoff?.zip ? errors?.payoff?.zip : ''}
            />

            <TextInputWithLabel
              label={translate('phone', TextTransform.CAPITALIZE)}
              placeholder={translate('phone', TextTransform.CAPITALIZE)}
              onChangeText={handleChange('payoff.phone')}
              onBlur={handleBlur('payoff.phone')}
              value={values.payoff.phone}
              errors={touched?.payoff?.phone ? errors?.payoff?.phone : ''}
              keyboardType="phone-pad"
              mask={Masks.USA_PHONE}
            />
          </>
        )}

        {/* Selling Info */}
        <>
          <SectionTitle text={translate('sellingInfo', TextTransform.CAPITALIZE)} />

          <CurrencyInput
            label="sellingPrice"
            placeholder={translate('sellingPrice', TextTransform.CAPITALIZE)}
            handleChange={handleChange('selling_info.price')}
            onBlur={handleBlur('selling_info.price')}
            value={values?.selling_info.price}
            error={touched.selling_info?.price ? errors.selling_info?.price : ''}
            keyboardType="decimal-pad"
            required
            type="currency"
            showErrorMsg={false}
          />

          <TextInputWithLabel
            label={translate('rebate', TextTransform.CAPITALIZE)}
            onChangeText={handleChange('selling_info.rebate')}
            onBlur={handleBlur('selling_info.rebate')}
            value={values?.selling_info.rebate}
            errors={touched.selling_info?.rebate ? errors.selling_info?.rebate : ''}
          />

          <TextInputWithLabel
            label={translate('trade', TextTransform.CAPITALIZE)}
            onChangeText={handleChange('selling_info.trade')}
            onBlur={handleBlur('selling_info.trade')}
            value={values?.selling_info.trade}
            errors={touched.selling_info?.trade ? errors.selling_info?.trade : ''}
          />

          <TextInputWithLabel
            label={translate('payoff', TextTransform.CAPITALIZE)}
            onChangeText={handleChange('selling_info.payoff')}
            onBlur={handleBlur('selling_info.payoff')}
            value={values?.selling_info.payoff}
            errors={touched.selling_info?.payoff ? errors.selling_info?.payoff : ''}
          />

          <CurrencyInput
            label="cashDown"
            placeholder={translate('cashDown', TextTransform.CAPITALIZE)}
            handleChange={handleChange('selling_info.cash_down')}
            onBlur={handleBlur('selling_info.cash_down')}
            value={values?.selling_info.cash_down}
            error={touched.selling_info?.cash_down ? errors.selling_info?.cash_down : ''}
            keyboardType="decimal-pad"
            type="currency"
            showErrorMsg={false}
          />

          <TextInputWithLabel
            label={translate('refundItem', TextTransform.CAPITALIZE)}
            onChangeText={handleChange('selling_info.refund_item')}
            onBlur={handleBlur('selling_info.refund_item')}
            value={values?.selling_info.refund_item}
            errors={touched.selling_info?.refund_item ? errors.selling_info?.refund_item : ''}
          />

          <CurrencyInput
            label="refundValue"
            placeholder={translate('refundValue', TextTransform.CAPITALIZE)}
            handleChange={handleChange('selling_info.refund_value')}
            onBlur={handleBlur('selling_info.refund_value')}
            value={values?.selling_info.refund_value}
            error={touched.selling_info?.refund_value ? errors.selling_info?.refund_value : ''}
            keyboardType="decimal-pad"
            type="currency"
            showErrorMsg={false}
          />

          <DropDownWithLabel
            label={`${translate('salesManagerName', TextTransform.CAPITAL)}*`}
            items={managers}
            customValue={values.selling_info.sales_manager_name}
            defaultValue={values.selling_info.sales_manager_name}
            onItemSelected={(item: any) => {
              setFieldValue('selling_info.sales_manager_name', item?.value);
            }}
            errors={touched.selling_info?.sales_manager_name ? errors.selling_info?.sales_manager_name : ""}
            style={{
              marginVertical: 8
            }}
          />

          <DropDownWithLabel
            label={`${translate('salesPersonName', TextTransform.CAPITAL)}*`}
            items={ownerList}
            customValue={values.selling_info.sales_person_name}
            defaultValue={values.selling_info.sales_person_name}
            onItemSelected={(item: any) => {
              setFieldValue('selling_info.sales_person_name', item?.value);
            }}
            errors={touched.selling_info?.sales_person_name ? errors.selling_info?.sales_person_name : ""}
            style={{
              marginVertical: 8
            }}
          />
        </>

        <ButtonContainer>
          <ButtonWithIcon
            loading={loading}
            text={translate('generate', TextTransform.CAPITAL)}
            onPress={async () => {
              const errorList = await validateForm();
              if (errorList) {
                console.log('errorList', errorList);
              }
              handleSubmit();
            }}
          />
        </ButtonContainer>
      </Content>
    </Container>
  );
};

export default React.memo(GenerateCoverLetterForm);
